import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User,
  Truck,
  MapPin,
  ClipboardList,
  ChevronDown,
  Check,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import { DISCOVERY_SOURCE_LABEL, type DiscoverySource } from '@iomi/shared';
import { CustomDropdown, type DropdownOption } from '../../components/ui/CustomDropdown';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { formatPhoneDigits } from '../../components/ui/PhoneInput';
import { useBookingForm, type BookingFormState, type AddressValue } from '../../hooks/useBookingForm';
import { bookingApi, type PlaceSuggestion } from '../../lib/bookingApi';

// ── Discovery source options ─────────────────────────────────────────────────

const BASE_DISCOVERY_SOURCE_OPTIONS: DropdownOption<string>[] = [
  { value: '', label: 'Select...' },
  ...(Object.keys(DISCOVERY_SOURCE_LABEL) as DiscoverySource[]).map(s => ({
    value: s as string,
    label: DISCOVERY_SOURCE_LABEL[s],
  })),
];

function buildDiscoveryOptions(customSources?: string[]): DropdownOption<string>[] {
  if (!customSources?.length) return BASE_DISCOVERY_SOURCE_OPTIONS;
  return [
    ...BASE_DISCOVERY_SOURCE_OPTIONS,
    ...customSources.map(s => ({ value: `custom:${s}`, label: s })),
  ];
}

// ── Bedroom dropdown options ────────────────────────────────────────────────

const BEDROOM_OPTIONS: DropdownOption<string>[] = [
  { value: '', label: 'Select...' },
  { value: '0', label: 'Studio / 0 Bedrooms' },
  { value: '1', label: '1 Bedroom' },
  { value: '2', label: '2 Bedrooms' },
  { value: '3', label: '3 Bedrooms' },
  { value: '4', label: '4 Bedrooms' },
  { value: '5', label: '5+ Bedrooms' },
];

// ── Section header (collapsible) ────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  complete,
  collapsed,
  onToggle,
}: {
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  title: string;
  complete: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const content = (
    <div className="flex items-center gap-3 w-full">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
          complete ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
        }`}
      >
        {complete ? <Check size={16} /> : <Icon size={16} />}
      </div>
      <h2 className="text-lg font-semibold text-primary-theme flex-1">{title}</h2>
      {onToggle && (
        <ChevronDown
          size={20}
          className={`text-slate shrink-0 transition-transform duration-200 ${
            collapsed ? '' : 'rotate-180'
          }`}
        />
      )}
    </div>
  );

  if (onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center min-h-[44px]"
      >
        {content}
      </button>
    );
  }

  return <div className="mb-4">{content}</div>;
}

// ── Form field helpers ───────────────────────────────────────────────────────

function FieldLabel({ htmlFor, children, required }: { htmlFor: string; children: React.ReactNode; required?: boolean }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-primary-theme mb-1">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

function TextInput({
  id,
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  required,
  inputMode,
}: {
  id: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  inputMode?: 'numeric' | 'tel' | 'email' | 'text';
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      inputMode={inputMode}
      className="w-full h-11 px-3 rounded-md border border-theme bg-surface text-primary-theme text-body placeholder:text-secondary-theme focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum transition-colors"
    />
  );
}

// ── Booking Address Search (uses public places endpoint) ─────────────────────

function BookingAddressSearch({
  value,
  zipCode,
  onAddressChange,
  onZipChange,
  label,
  id,
}: {
  value: AddressValue | null;
  zipCode: string;
  onAddressChange: (v: AddressValue | null) => void;
  onZipChange: (v: string) => void;
  label: string;
  id: string;
}) {
  const [mode, setMode] = useState<'address' | 'zip'>(value ? 'address' : zipCode ? 'zip' : 'address');
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fetching, setFetching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!input.trim()) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setFetching(true);
    try {
      const res = await bookingApi.placesAutocomplete(input);
      setSuggestions(res.data);
      setShowDropdown(res.data.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setFetching(false);
    }
  }, []);

  function handleQueryChange(v: string) {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  }

  function handleSelect(s: PlaceSuggestion) {
    onAddressChange({ address: s.description, placeId: s.placeId });
    onZipChange(''); // clear zip when address is selected
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
  }

  function handleClear() {
    onAddressChange(null);
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
  }

  function switchToZip() {
    setMode('zip');
    onAddressChange(null);
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
  }

  function switchToAddress() {
    setMode('address');
    onZipChange('');
  }

  return (
    <div ref={containerRef}>
      <FieldLabel htmlFor={id} required>{label}</FieldLabel>

      {mode === 'address' ? (
        <>
          {value ? (
            <div className="flex items-start gap-2 rounded-md border border-plum/30 bg-plum/5 px-3 py-2.5">
              <MapPin size={15} className="shrink-0 text-plum mt-0.5" />
              <span className="flex-1 text-sm text-primary-theme leading-snug">{value.address}</span>
              <button
                type="button"
                onClick={handleClear}
                className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-slate hover:text-midnight transition-colors"
                aria-label="Clear address"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                {fetching
                  ? <div className="w-3.5 h-3.5 border-2 border-slate/30 border-t-slate rounded-full animate-spin" />
                  : <Search size={14} className="text-slate" />
                }
              </div>
              <input
                id={id}
                type="text"
                autoComplete="off"
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                placeholder="Start typing an address…"
                className="w-full h-11 pl-9 pr-3 rounded-md border border-theme bg-surface text-primary-theme text-body placeholder:text-secondary-theme focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum transition-colors"
              />
              {showDropdown && (
                <ul
                  role="listbox"
                  className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-stone rounded-lg shadow-brand-md overflow-hidden"
                >
                  {suggestions.map(s => (
                    <li key={s.placeId} role="option" aria-selected="false">
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
                        className="w-full flex items-start gap-2.5 px-3 py-3 text-left hover:bg-mist active:bg-stone transition-colors border-b border-stone last:border-b-0"
                      >
                        <MapPin size={14} className="shrink-0 text-slate mt-0.5" />
                        <span className="text-sm text-primary-theme leading-snug">{s.description}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={switchToZip}
            className="mt-1.5 text-xs text-plum hover:underline"
          >
            Use zip code instead
          </button>
        </>
      ) : (
        <>
          <TextInput
            id={id}
            inputMode="numeric"
            value={zipCode}
            onChange={onZipChange}
            placeholder="Enter zip code"
            required
          />
          <button
            type="button"
            onClick={switchToAddress}
            className="mt-1.5 text-xs text-plum hover:underline"
          >
            Search by address instead
          </button>
        </>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function BookingFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Collapsed state for each section
  const [contactCollapsed, setContactCollapsed] = useState(false);
  const [moveDetailsCollapsed, setMoveDetailsCollapsed] = useState(false);
  const [additionalCollapsed, setAdditionalCollapsed] = useState(true);

  const {
    orgProfile,
    loading,
    orgError,
    form,
    updateField,
    effectiveType,
    isMoveTypeComplete,
    isContactComplete,
    isMoveDetailsComplete,
    isFormComplete,
    submitting,
    submitForm,
    estimating,
    calculateEstimate,
    bookingToken,
  } = useBookingForm(slug ?? '');

  // ── Loading / Error states ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist">
        <LoadingSpinner />
      </div>
    );
  }

  if (orgError || !orgProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mist px-4">
        <div className="bg-white rounded-lg shadow-brand-md p-8 text-center max-w-sm">
          <h1 className="text-xl font-semibold text-midnight mb-2">Page Not Found</h1>
          <p className="text-slate text-body">
            This booking page is not available. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  const brandPrimary = orgProfile.bookingConfig?.brandPrimaryColor ?? '#6B2D8B';
  const brandText = orgProfile.bookingConfig?.brandTitleTextColor ?? '#FFFFFF';
  const showPriceEstimate = orgProfile.bookingConfig?.enableShowPriceEstimate !== false && !form.hasSpecialtyItems;

  const showMoveTypeSection = orgProfile.acceptsCommercial && orgProfile.acceptsResidential;
  const isResidential = effectiveType === 'residential';
  const isCommercial = effectiveType === 'commercial';

  async function handleViewEstimate() {
    if (showPriceEstimate) {
      const result = await calculateEstimate();
      if (result && bookingToken) {
        navigate(`/${slug}/estimate`, {
          state: { estimate: result, bookingToken, orgProfile },
        });
      }
    } else {
      // No price estimate — submit form and go straight to confirmation
      const token = await submitForm();
      if (token) {
        try {
          await bookingApi.recordAction(slug!, token, 'request_estimate');
        } catch {
          // best-effort
        }
        navigate(`/${slug}/confirmation`, {
          state: { bookingToken: token, orgProfile },
        });
      }
    }
  }

  return (
    <div className="min-h-screen bg-mist pb-24">

      {/* Header */}
      <header
        className="border-b border-stone px-4 py-4"
        style={{
          backgroundColor: brandPrimary,
          color: brandText,
        }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {orgProfile.logoBase64 ? (
            <img
              src={orgProfile.logoBase64}
              alt={orgProfile.name}
              className="w-10 h-10 rounded-md object-contain shrink-0"
            />
          ) : (
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center font-bold text-lg shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: brandText }}
            >
              {orgProfile.name.charAt(0)}
            </div>
          )}
          <div>
            <h1 className="text-lg font-bold leading-tight" style={{ color: brandText }}>{orgProfile.name}</h1>
            <p className="text-sm" style={{ color: brandText, opacity: 0.8 }}>Get a free moving estimate</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-5">

        {/* Welcome message from booking config */}
        {orgProfile.bookingConfig?.welcomeMessage && (
          <div className="bg-plum/5 border border-plum/20 rounded-lg p-4">
            <p className="text-sm text-midnight">{orgProfile.bookingConfig.welcomeMessage}</p>
          </div>
        )}

        {/* ── Section 1: Move Type ─────────────────────────────────── */}
        {showMoveTypeSection && (
          <section className="bg-white rounded-lg shadow-brand-sm p-5">
            <SectionHeader icon={Truck} title="Move Type" complete={isMoveTypeComplete} />
            <div className="flex rounded-md border border-theme overflow-hidden">
              <button
                type="button"
                onClick={() => updateField('type', 'residential')}
                className={`flex-1 h-11 text-sm font-medium transition-colors ${
                  form.type === 'residential'
                    ? 'bg-plum text-white'
                    : 'bg-surface text-secondary-theme hover:bg-stone/50'
                }`}
              >
                Residential
              </button>
              <button
                type="button"
                onClick={() => updateField('type', 'commercial')}
                className={`flex-1 h-11 text-sm font-medium border-l border-theme transition-colors ${
                  form.type === 'commercial'
                    ? 'bg-plum text-white'
                    : 'bg-surface text-secondary-theme hover:bg-stone/50'
                }`}
              >
                Commercial
              </button>
            </div>
          </section>
        )}

        {/* ── Section 2: Contact Information ───────────────────────── */}
        {(!showMoveTypeSection || effectiveType) && (
        <section className="bg-white rounded-lg shadow-brand-sm p-5">
          <SectionHeader
            icon={User}
            title="Contact Information"
            complete={isContactComplete}
            collapsed={contactCollapsed}
            onToggle={() => setContactCollapsed(!contactCollapsed)}
          />
          {!contactCollapsed && (
            <div className="space-y-4 mt-4">
              <div>
                <FieldLabel htmlFor="name" required>Name</FieldLabel>
                <TextInput
                  id="name"
                  value={form.name}
                  onChange={v => updateField('name', v)}

                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <FieldLabel htmlFor="email">
                  Email{!form.phone.trim() && <span className="text-red-500 ml-0.5">*</span>}
                </FieldLabel>
                <TextInput
                  id="email"
                  type="email"
                  inputMode="email"
                  value={form.email}
                  onChange={v => updateField('email', v)}

                  placeholder="you@example.com"
                />
              </div>

              <div>
                <FieldLabel htmlFor="phone">
                  Phone{!form.email.trim() && <span className="text-red-500 ml-0.5">*</span>}
                </FieldLabel>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  value={form.phone}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    updateField('phone', formatPhoneDigits(digits, 'US'));
                  }}
                  onKeyDown={e => {
                    // Allow control keys but block non-numeric characters
                    if (
                      e.key.length === 1 &&
                      !/\d/.test(e.key) &&
                      !e.ctrlKey && !e.metaKey
                    ) {
                      e.preventDefault();
                    }
                  }}

                  placeholder="(555) 123-4567"
                  className="w-full h-11 px-3 rounded-md border border-theme bg-surface text-primary-theme text-body placeholder:text-secondary-theme focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum transition-colors"
                />
              </div>

              {isCommercial && (
                <div>
                  <FieldLabel htmlFor="businessName">Business Name</FieldLabel>
                  <TextInput
                    id="businessName"
                    value={form.businessName}
                    onChange={v => updateField('businessName', v)}
                    placeholder="Company name"
                  />
                </div>
              )}

              <div>
                <FieldLabel htmlFor="discoverySource">How did you hear about us?</FieldLabel>
                <CustomDropdown
                  options={buildDiscoveryOptions(orgProfile.bookingConfig?.customDiscoverySources)}
                  value={form.discoverySource}
                  onChange={v => updateField('discoverySource', v as BookingFormState['discoverySource'])}
                  placeholder="Select..."
                />
              </div>

              {form.discoverySource === 'referral' && (
                <div>
                  <FieldLabel htmlFor="referredBy">Referred by</FieldLabel>
                  <TextInput
                    id="referredBy"
                    value={form.referredBy}
                    onChange={v => updateField('referredBy', v)}
                    placeholder="Who referred you?"
                  />
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ── Section 3: Move Details ──────────────────────────────── */}
        {(!showMoveTypeSection || effectiveType) && (
        <section className="bg-white rounded-lg shadow-brand-sm p-5">
          <SectionHeader
            icon={MapPin}
            title="Move Details"
            complete={isMoveDetailsComplete}
            collapsed={moveDetailsCollapsed}
            onToggle={() => setMoveDetailsCollapsed(!moveDetailsCollapsed)}
          />
          {!moveDetailsCollapsed && (
            <div className="space-y-4 mt-4">
              <div>
                <FieldLabel htmlFor="preferredMoveDate">Preferred Move Date</FieldLabel>
                <input
                  id="preferredMoveDate"
                  type="date"
                  value={form.preferredMoveDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => {
                    updateField('preferredMoveDate', e.target.value);
                    if (e.target.value) updateField('estimatedMoveDateRange', '');
                  }}
                  className="w-full h-11 px-3 rounded-md border border-theme bg-surface text-primary-theme text-body focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum transition-colors"
                />
                <p className="text-xs text-secondary-theme mt-1">Or provide an estimated range below</p>
              </div>

              <div>
                <FieldLabel htmlFor="estimatedMoveDateRange">Estimated Move Timeframe</FieldLabel>
                <TextInput
                  id="estimatedMoveDateRange"
                  value={form.estimatedMoveDateRange}
                  onChange={v => {
                    updateField('estimatedMoveDateRange', v);
                    if (v.trim()) updateField('preferredMoveDate', '');
                  }}
                  placeholder='e.g. "Mid April" or "Summer 2026"'
                />
              </div>

              <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.flexibleDate}
                  onChange={e => updateField('flexibleDate', e.target.checked)}
                  className="w-5 h-5 rounded border-theme text-plum focus:ring-plum/30 shrink-0"
                />
                <span className="text-sm text-primary-theme">My dates are flexible</span>
              </label>

              <hr className="border-stone" />

              <BookingAddressSearch
                value={form.originAddress}
                zipCode={form.originZipCode}
                onAddressChange={v => updateField('originAddress', v)}
                onZipChange={v => updateField('originZipCode', v)}
                label="Moving From"
                id="originAddress"
              />

              <BookingAddressSearch
                value={form.destinationAddress}
                zipCode={form.destinationZipCode}
                onAddressChange={v => updateField('destinationAddress', v)}
                onZipChange={v => updateField('destinationZipCode', v)}
                label="Moving To"
                id="destinationAddress"
              />

              <hr className="border-stone" />

              {isCommercial && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel htmlFor="numberOfEmployees" required>Number of Employees</FieldLabel>
                    <TextInput
                      id="numberOfEmployees"
                      inputMode="numeric"
                      value={form.numberOfEmployees}
                      onChange={v => updateField('numberOfEmployees', v)}
                      placeholder="Headcount"
                      required
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="squareFootage">Approx. Sq. Footage</FieldLabel>
                    <TextInput
                      id="squareFootage"
                      inputMode="numeric"
                      value={form.squareFootage}
                      onChange={v => updateField('squareFootage', v)}
                      placeholder="Square footage"
                    />
                  </div>
                </div>
              )}

              {isResidential && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel htmlFor="bedrooms" required>Bedrooms</FieldLabel>
                    <CustomDropdown
                      id="bedrooms"
                      options={BEDROOM_OPTIONS}
                      value={form.bedrooms}
                      onChange={v => updateField('bedrooms', v)}
                      placeholder="Select..."
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="squareFootageRes">Approx. Sq. Footage</FieldLabel>
                    <TextInput
                      id="squareFootageRes"
                      inputMode="numeric"
                      value={form.squareFootage}
                      onChange={v => updateField('squareFootage', v)}
                      placeholder="Sq. footage"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* ── Section 4: Additional Details (residential only) ─────── */}
        {(!showMoveTypeSection || effectiveType) && isResidential && (
          <section className="bg-white rounded-lg shadow-brand-sm p-5">
            <SectionHeader
              icon={ClipboardList}
              title="Additional Details"
              complete={!!(form.originFlightsOfStairs || form.destinationFlightsOfStairs || form.elevatorAccess || form.longCarry || form.hasSpecialtyItems)}
              collapsed={additionalCollapsed}
              onToggle={() => setAdditionalCollapsed(!additionalCollapsed)}
            />
            {additionalCollapsed && (
              <p className="text-xs text-secondary-theme mt-1 ml-11">
                Providing additional details helps us give you a more accurate estimate
              </p>
            )}
            {!additionalCollapsed && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel htmlFor="originFlightsOfStairs">Flights of Stairs (Origin)</FieldLabel>
                    <TextInput
                      id="originFlightsOfStairs"
                      inputMode="numeric"
                      value={form.originFlightsOfStairs}
                      onChange={v => updateField('originFlightsOfStairs', v)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="destinationFlightsOfStairs">Flights of Stairs (Dest.)</FieldLabel>
                    <TextInput
                      id="destinationFlightsOfStairs"
                      inputMode="numeric"
                      value={form.destinationFlightsOfStairs}
                      onChange={v => updateField('destinationFlightsOfStairs', v)}
                      placeholder="0"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.elevatorAccess}
                    onChange={e => updateField('elevatorAccess', e.target.checked)}
                    className="w-5 h-5 rounded border-theme text-plum focus:ring-plum/30 shrink-0"
                  />
                  <span className="text-sm text-primary-theme">Elevator access required</span>
                </label>

                <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.longCarry}
                    onChange={e => updateField('longCarry', e.target.checked)}
                    className="w-5 h-5 rounded border-theme text-plum focus:ring-plum/30 shrink-0"
                  />
                  <div>
                    <span className="text-sm text-primary-theme block">Long distance to loading zone</span>
                    <span className="text-xs text-secondary-theme">More than ~75ft from parking to entrance</span>
                  </div>
                </label>

                {orgProfile.specialtyItemsEnabled && (
                  <>
                    <hr className="border-stone" />
                    <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.hasSpecialtyItems}
                        onChange={e => updateField('hasSpecialtyItems', e.target.checked)}
                        className="w-5 h-5 rounded border-theme text-plum focus:ring-plum/30 shrink-0"
                      />
                      <div>
                        <span className="text-sm text-primary-theme block">Specialty Items</span>
                        <span className="text-xs text-secondary-theme">
                          Pianos, safes, pool tables, hot tubs, large appliances, antiques, or anything requiring special handling
                        </span>
                      </div>
                    </label>
                    {form.hasSpecialtyItems && (
                      <div>
                        <FieldLabel htmlFor="specialtyItemsDetails">Tell us about your specialty items</FieldLabel>
                        <textarea
                          id="specialtyItemsDetails"
                          value={form.specialtyItemsDetails}
                          onChange={e => updateField('specialtyItemsDetails', e.target.value)}
                          placeholder="Describe your specialty items (e.g. upright piano, 500lb safe, antique armoire…)"
                          rows={3}
                          className="w-full px-3 py-2.5 rounded-md border border-theme bg-surface text-primary-theme text-body placeholder:text-secondary-theme focus:outline-none focus:ring-2 focus:ring-plum/30 focus:border-plum transition-colors resize-none"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* ── Sticky CTA button ──────────────────────────── */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone px-4 py-4 pb-safe-bottom z-40">
        <div className="max-w-lg mx-auto mb-1">
          <button
            type="button"
            disabled={!isFormComplete || estimating || submitting}
            onClick={handleViewEstimate}
            className="w-full h-12 rounded-md font-semibold text-body transition-all disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] flex items-center justify-center gap-2"
            style={{ backgroundColor: brandPrimary, color: brandText }}
          >
            {estimating || submitting ? (
              <><Loader2 size={18} className="animate-spin" /> {estimating ? 'Calculating...' : 'Submitting...'}</>
            ) : showPriceEstimate ? (
              'View Estimate'
            ) : (
              'Request a Free Estimate'
            )}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-4 pb-20">
        <span className="text-xs text-slate">Powered by <span className="font-semibold">IOMI Pro</span></span>
      </div>
    </div>
  );
}
